import axios from "axios";
import React from "react";
import OtpInput from "react-otp-input";

import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import lock_img from "../../assets/password.svg";
import { AuthHeading, PrimaryAuthButton, StepBar } from "../../components/auth/AuthShell";
import { TwInputGroup } from "../../components/tailwind";
import { classNames } from "../../helpers";
import { getUrlForUsers } from "../../utils/confighelpers";

/**
 * First sign-in with a temporary password (same journey as the mobile apps).
 *
 * When a club creates a member, the member gets a temporary password. Logging in with it
 * returns 200 with `isVerified: false`, no token, and the message
 * "You are logging-in via a temporary password. Please verify your mobile number and set a new password to continue".
 * The member then has to:
 *   1. get a code by SMS            POST /users/forgotPassword        { username, userType } -> token
 *   2. enter the code               PUT  /users/verifyForgotPasswordOtp { code, token }    -> token
 *   3. choose their own password    PUT  /users/resetForgotPassword  { password, token }   (also sets isVerified: true)
 *   4. be signed in with it         -> onSignIn(newPassword), the normal login
 * The number is the one they just logged in with, so it is not asked again.
 * The backend gives 2 minutes for each of steps 2 and 3.
 */

const MIN_PASSWORD_LENGTH = 6;

/** Recognises the backend's temporary-password message (both fragments required, like the apps). */
export function saysTemporaryPassword(message) {
  const text = String(message || "").toLowerCase();
  return text.includes("temporary password") && (text.includes("verify your mobile") || text.includes("set a new password"));
}

/** True when a loginUser response means "temporary password: verify first" rather than a real session. */
export function needsVerification(response) {
  const data = response?.data;
  return (data && typeof data === "object" && data.isVerified === false && !data.token) || saysTemporaryPassword(response?.message);
}

const otpInputClass = (error) =>
  classNames(
    "!h-[68px] !w-16 text-[28px] font-black text-gray-900 rounded-2xl bg-[#F5F4F6] outline outline-1 -outline-offset-1 focus-within:bg-white focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2",
    error ? "text-red-900 outline-red-300 focus-within:outline-red-600" : "outline-gray-300 hover:outline-indigo-bz focus-within:outline-indigo-bz"
  );

export default function VerifyTempPassword({ username, userType = "student", fullName, displayNumber, message, onSignIn, onCancel }) {
  const [step, setStep] = React.useState("intro"); // intro | code | password
  const [token, setToken] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({});
  // The reset token is single-use: once the reset worked, a retry must only sign in again.
  const resetDone = React.useRef(false);

  const firstName = String(fullName || "").trim().split(" ")[0];

  const sendCode = async (isResend = false) => {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      const res = await axios.post(`${getUrlForUsers()}forgotPassword`, { username, userType });
      const t = res?.data?.data?.token;
      if (res?.data?.statusCode === 200 && t) {
        setToken(t);
        setOtp("");
        setStep("code");
        if (isResend) setNotice("We've sent you a new code.");
        return true;
      }
      setError(res?.data?.message || "We couldn't send a code to this number. Please try again.");
    } catch (e) {
      setError("We couldn't send a code right now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
    return false;
  };

  const verifyCode = async () => {
    setError("");
    setNotice("");
    if (otp.length !== 4) {
      setError("Enter the 4-digit code from the SMS.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.put(`${getUrlForUsers()}verifyForgotPasswordOtp`, { code: otp, token });
      const t = res?.data?.data?.token;
      if (res?.data?.statusCode === 200 && t) {
        setToken(t);
        setPassword("");
        setConfirm("");
        setFieldErrors({});
        resetDone.current = false;
        setStep("password");
      } else {
        const msg = String(res?.data?.message || "");
        setError(/expired/i.test(msg) ? "That code has expired. Send a new code and try again." : "That code isn't right. Check the SMS and try again.");
      }
    } catch (e) {
      setError("We couldn't check the code right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    setError("");
    const errs = {};
    if (password.length < MIN_PASSWORD_LENGTH) errs.newPassword = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (!confirm) errs.confirmPassword = "Please repeat your new password.";
    else if (confirm !== password) errs.confirmPassword = "The passwords don't match.";
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      if (!resetDone.current) {
        const res = await axios.put(`${getUrlForUsers()}resetForgotPassword`, { password, token });
        if (res?.data?.statusCode !== 200) {
          const msg = res?.data?.message;
          setError(
            typeof msg === "string" && msg
              ? `${msg}${/expired/i.test(msg) ? " Please request a new code." : ""}`
              : "We couldn't save your password. Please request a new code and try again."
          );
          return;
        }
        resetDone.current = true;
      }
      // Password is set and the account is now verified: sign in normally with it.
      const ok = await onSignIn(password);
      if (ok === false) setError("Your password is saved. Please sign in with your mobile number and new password.");
    } catch (e) {
      setError(resetDone.current ? "Your password is saved, but signing you in failed. Please sign in with your new password." : "We couldn't save your password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const eye = (
    <button type="button" className="flex items-center" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword((v) => !v)}>
      {showPassword ? <EyeIcon className="size-5" /> : <EyeSlashIcon className="size-5" />}
    </button>
  );

  return (
    <div className="flex flex-col gap-6">
      <StepBar step={step === "intro" ? 1 : step === "code" ? 2 : 3} />
      <div>
        <AuthHeading title={step === "intro" ? (firstName ? `Hi ${firstName}!` : "Welcome!") : step === "code" ? "Enter the code" : "Choose your password"} />
        <p className="mt-2 text-base text-gray-600 sm:text-[17px]">
          {step === "intro" &&
            (message && saysTemporaryPassword(message)
              ? message.replace(/\s*$/, "").replace(/([^.])$/, "$1.")
              : "You are signing in with a temporary password. Please verify your mobile number and set a new password to continue.")}
          {step === "code" && (
            <>
              We sent a 4-digit code by SMS to <span className="whitespace-nowrap font-extrabold text-gray-900">{displayNumber || username}</span>.
            </>
          )}
          {step === "password" && "Your number is verified. Now set the password you'll use from now on, on the web and in the app."}
        </p>
      </div>

      {step === "intro" && (
        <PrimaryAuthButton type="button" onClick={() => sendCode(false)} busy={busy}>
          Send me a code
        </PrimaryAuthButton>
      )}

      {step === "code" && (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            verifyCode();
          }}
        >
          <OtpInput
            value={otp}
            onChange={(v) => {
              setOtp(v);
              if (error) setError("");
            }}
            numInputs={4}
            containerStyle="flex gap-3"
            inputStyle={otpInputClass(!!error)}
            renderInput={(props) => <input {...props} autoComplete="one-time-code" aria-label="Verification code digit" />}
            shouldAutoFocus
            inputType="number"
          />
          <p className="text-[15px] text-gray-600">
            Didn't get a code?{" "}
            <button type="button" disabled={busy} onClick={() => sendCode(true)} className="font-extrabold text-indigo-bz hover:underline disabled:opacity-50">
              Send a new code
            </button>
          </p>
          <PrimaryAuthButton busy={busy} disabled={otp.length !== 4}>
            Verify code
          </PrimaryAuthButton>
        </form>
      )}

      {step === "password" && (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            savePassword();
          }}
        >
          <TwInputGroup
            label="New password"
            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
            name="newPassword"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            autoFocus
            value={password}
            leftIcon={<img src={lock_img} alt="" />}
            rightIcon={eye}
            onChange={(e) => setPassword(e.target.value)}
            errors={fieldErrors}
          />
          <TwInputGroup
            label="Repeat new password"
            placeholder="Repeat your new password"
            name="confirmPassword"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            leftIcon={<img src={lock_img} alt="" />}
            onChange={(e) => setConfirm(e.target.value)}
            errors={fieldErrors}
          />
          <PrimaryAuthButton busy={busy}>Save password and sign in</PrimaryAuthButton>
        </form>
      )}

      {notice && !error && <p className="rounded-xl bg-green-50 px-4 py-3 text-center text-sm text-green-800">{notice}</p>}
      {error && (
        <div role="alert" className="flex flex-col items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-center text-sm text-red-700">
          <span>{error}</span>
          {step === "password" && !resetDone.current && /expired|new code|invalid request/i.test(error) && (
            <button type="button" disabled={busy} onClick={() => sendCode(true)} className="font-bold text-indigo-bz underline-offset-2 hover:underline">
              Send a new code
            </button>
          )}
        </div>
      )}

      <p className="text-center text-[15px] text-gray-600">
        <button type="button" onClick={onCancel} disabled={busy} className="font-extrabold text-indigo-bz hover:underline disabled:opacity-50">
          Back to sign in
        </button>
      </p>
    </div>
  );
}
